import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Loader2, HelpCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { showIntercomAnonymous } from '@/components/IntercomProvider';

const loginSchema = z.object({
  emailOrUsername: z.string().min(1, 'Email or username is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginAlternative() {
  const navigate = useNavigate();
  const { signIn, isAuthenticated, user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (isAuthenticated || user) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setError(null);

    try {
      await signIn(data.emailOrUsername, data.password);
      navigate('/dashboard');
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-zinc-100 dark:bg-zinc-950">
      {/* Header Bar */}
      <header className="bg-zinc-950 dark:bg-black text-white px-6 py-3 flex items-center justify-between border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
        </div>
        <button
          type="button"
          onClick={showIntercomAnonymous}
          className="text-sm text-zinc-300 hover:text-white transition-colors flex items-center gap-1"
        >
          <HelpCircle className="h-4 w-4" />
          Help
        </button>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Side - Login Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white dark:bg-zinc-900">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
                Log in to BlazePOS
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 uppercase tracking-wide font-medium">
                Point of Sale System
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="emailOrUsername" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Email or Username
                </Label>
                <div className="relative">
                  <Input
                    id="emailOrUsername"
                    type="text"
                    placeholder="Enter your email or username"
                    className="h-11 px-4 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-primary focus:ring-primary"
                    {...register('emailOrUsername')}
                  />
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                </div>
                {errors.emailOrUsername && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.emailOrUsername.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className="h-11 px-4 pr-10 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-primary focus:ring-primary"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between pt-2">
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary hover:text-primary/80 hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-11 font-medium rounded-lg transition-colors"
                disabled={isLoading || isAuthenticated}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Log in'
                )}
              </Button>
            </form>

            <p className="mt-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
              Need help accessing your account?{' '}
              <a
                href="mailto:support@blazepointofsale.com"
                className="text-primary hover:underline"
              >
                Contact support
              </a>
            </p>
          </div>
        </div>

        {/* Right Side - Hero Image */}
        <div className="hidden lg:flex lg:w-1/2 blaze-gradient items-center justify-center p-12 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
          
          {/* Content */}
          <div className="relative z-10 text-center">
            {/* POS Terminal Illustration */}
            <div className="relative mx-auto w-80 h-80 mb-8">
              {/* Monitor */}
              <div className="absolute inset-x-4 top-0 h-56 bg-zinc-900 rounded-2xl shadow-2xl border-4 border-zinc-800 overflow-hidden">
                {/* Screen */}
                <div className="m-3 h-44 bg-zinc-800 rounded-lg overflow-hidden">
                  {/* Mock POS Interface */}
                  <div className="p-2 h-full flex">
                    {/* Sidebar */}
                    <div className="w-8 bg-zinc-700/50 rounded mr-2 flex flex-col gap-1 p-1">
                      <div className="w-full h-4 bg-orange-500/80 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                    </div>
                    {/* Main content */}
                    <div className="flex-1 grid grid-cols-3 gap-1">
                      <div className="bg-purple-500/60 rounded" />
                      <div className="bg-amber-500/60 rounded" />
                      <div className="bg-green-500/60 rounded" />
                      <div className="bg-blue-500/60 rounded" />
                      <div className="bg-rose-500/60 rounded" />
                      <div className="bg-cyan-500/60 rounded" />
                      <div className="bg-indigo-500/60 rounded" />
                      <div className="bg-orange-500/60 rounded" />
                      <div className="bg-teal-500/60 rounded" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Stand */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-16 bg-zinc-800 rounded-b-xl" />
              {/* Base */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-4 bg-zinc-900 rounded-full shadow-lg" />
            </div>

            <h2 className="text-2xl font-semibold text-white mb-3">
              Modern Point of Sale
            </h2>
            <p className="text-white/80 text-lg max-w-sm mx-auto">
              Streamline your business with our powerful, intuitive POS system
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Real-time Analytics
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Multi-location
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Cloud-based
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Desktop & Mobile Apps
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-zinc-50 dark:bg-zinc-800 border-t border-zinc-200 dark:border-zinc-700 px-6 py-4">
        <div className="flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-400">
          <p>BlazePOS | Version {import.meta.env.VITE_APP_VERSION as string}</p>
          <div className="flex items-center gap-4">
            <a href="https://www.blazepointofsale.com/privacy" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Privacy Policy
            </a>
            <a href="https://www.blazepointofsale.com/terms" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

